import styled from "@emotion/styled";
import { fromLamports, NATIVE_SOL } from "@stepfinance/common";
import type { StepColumnsType } from "@stepfinance/react";
import { getColumnSearchProps, imageFromPlatformName, StepTable, URLIcon } from "@stepfinance/react";
import type { FilterValue, TablePaginationConfig } from "antd/lib/table/interface";
import React, { useEffect, useMemo, useState } from "react";

import { ReactComponent as ValidatorStakingIcon } from "../../../assets/validator-staking.svg";
import { useLanguageContext } from "../../../contexts/language/language";
import { useMarkets } from "../../../contexts/market";
import { useValidatorStakingContext } from "../../../contexts/validorStaking";
import type { CollapseControlProps } from "../../../hooks/home/useCollapseControl";
import { isPaginationHidden, useLocalStorageState } from "../../../utils/utils";
import { ExplorerLinkDisplay } from "../../ExplorerLink/ExplorerLinkDisplay";
import { IconTextContainer, PAGE_SIZE_OPTIONS, TokenIconText } from "../../shared";
import CellValue from "../../Table/CellValue";
import { ChevronCollapse } from "../ChevronCollapse/ChevronCollapse";

interface ValidatorStakingPosition {
  key: number;
  platform: string;
  address: string;
  validator: string;
  balance: number;
  value: number | null;
}

export const ValidatorStaking: React.FC<CollapseControlProps> = ({ activePanels, getPanelKey, onPanelClick }) => {
  const { priceInUSD } = useMarkets();
  const { stakeAccounts, setValidatorStakingBalance } = useValidatorStakingContext();
  const [pagination, setPagination] = useState<TablePaginationConfig>({});
  const [filteredInfo, setFilteredInfo] = useState<Record<string, FilterValue | null>>({});
  const [paginationSetting, setPaginationSetting] = useLocalStorageState(
    "validatorStakingPaginationSizeSetting",
    PAGE_SIZE_OPTIONS.m[0],
  );

  const {
    currentLanguageData: {
      common,
      pages: {
        dashboard: {
          modules: { validatorStaking: validatorStakingText },
        },
      },
    },
  } = useLanguageContext();

  useEffect(() => {
    setPagination({
      hideOnSinglePage: isPaginationHidden(stakeAccounts.length, PAGE_SIZE_OPTIONS.m),
      showSizeChanger: true,
      pageSizeOptions: PAGE_SIZE_OPTIONS.m,
      pageSize: parseInt(paginationSetting),
    });
  }, [stakeAccounts.length, paginationSetting]);

  const totalStakedValue = useMemo(() => {
    const solPrice = priceInUSD(NATIVE_SOL.toBase58());
    return stakeAccounts.reduce((total, stakeAccount) => {
      const balanceFromLamports = fromLamports(stakeAccount.lamports, 9);
      const value = (solPrice ?? 0) * balanceFromLamports;
      return total + value;
    }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stakeAccounts.length, priceInUSD]);

  useEffect(() => {
    setValidatorStakingBalance(totalStakedValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalStakedValue]);

  const dataSource = useMemo(
    () =>
      stakeAccounts.map((stakeAccount, i) => {
        const balanceFromLamports = fromLamports(stakeAccount.lamports, 9);
        const solPrice = priceInUSD(NATIVE_SOL.toBase58());

        return {
          key: i,
          platform: "Solana",
          address: stakeAccount.address.toBase58(),
          validator: stakeAccount.stakeAccount.info.stake?.delegation.voter.toBase58() || "",
          // apy: "TBD%", TODO: Calculate APY
          balance: balanceFromLamports,
          value: solPrice ? solPrice * balanceFromLamports : null,
        };
      }),
    [stakeAccounts, priceInUSD],
  );

  const columns: StepColumnsType<ValidatorStakingPosition> = [
    {
      title: common.platform,
      dataIndex: "platform",
      key: "platform",
      render: (platform) => (
        <IconTextContainer>
          <URLIcon src={imageFromPlatformName(platform) ?? ""} />
          <TokenIconText bold>{platform}</TokenIconText>
        </IconTextContainer>
      ),
    },
    {
      title: common.address,
      dataIndex: "address",
      key: "address",
      render: (address) => <ExplorerLinkDisplay address={address} addressLength={4} type="address" />,
      ...getColumnSearchProps("address", "address"),
      filteredValue: filteredInfo.address ?? null,
    },
    {
      title: common.validator,
      dataIndex: "validator",
      key: "validator",
      render: (validator) => <ExplorerLinkDisplay address={validator} addressLength={4} type="address" />,
      ...getColumnSearchProps("validator", "validator"),
      filteredValue: filteredInfo.validator ?? null,
    },
    {
      title: validatorStakingText.solAmount,
      dataIndex: "balance",
      key: "balance",
      className: "column-number",
      render: (balance) => <CellValue cellType="number">{balance}</CellValue>,
      sorter: (a, b) => a.balance - b.balance,
    },
    {
      title: common.value,
      dataIndex: "value",
      key: "value",
      className: "column-number",
      defaultSortOrder: "descend",
      render: (value) => (
        <CellValue cellType="value" fontWeight="bold">
          {value}
        </CellValue>
      ),
      sorter: (a, b) => (a.value ?? 0) - (b.value ?? 0),
    },
  ];

  return (
    <>
      {dataSource.length ? (
        <ValidatorStakingContainer>
          <ChevronCollapse
            title={validatorStakingText.title}
            TitleIcon={ValidatorStakingIcon}
            value={totalStakedValue}
            activePanels={activePanels}
            panelKey={getPanelKey("ValidatorStaking")}
            onChange={onPanelClick}
          >
            <StepTable
              columns={columns}
              dataSource={dataSource}
              pagination={pagination}
              onChange={(pagination, filters) => {
                setPaginationSetting(pagination.pageSize ? pagination.pageSize.toString() : PAGE_SIZE_OPTIONS.m[0]);
                setFilteredInfo(filters);
              }}
            />
          </ChevronCollapse>
        </ValidatorStakingContainer>
      ) : null}
    </>
  );
};

const ValidatorStakingContainer = styled.div`
  margin-top: 20px;
`;
